import React, { useCallback, useState } from 'react';
import { MapPin, Car, Route, Clock, Save, RefreshCw, Download, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import RouteParametersSection from './RouteParametersSection';
import VehicleParametersSection from './VehicleParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import StopsSection from './StopsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { calculateDateDifference } from '../../../../../utils/calculations/date';

/**
 * Компонент основной формы расчета времени в пути
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  routeDetails,
  setRouteDetails,
  vehicleTypes,
  roadTypes,
  trafficLevels,
  weatherConditions,
  saveRoute 
}) => {
  
  // Состояние для имени сохраняемого маршрута
  const [saveRouteName, setSaveRouteName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Обновляем среднюю скорость автоматически при изменении типа транспорта
      if (name === 'vehicleType') {
        const selectedVehicleType = vehicleTypes.find(type => type.id === fieldValue);
        if (selectedVehicleType) {
          const currentSpeed = parseFloatOrDefault(prev.averageSpeed, 0);
          const isSpeedDefault = vehicleTypes.some(vt => currentSpeed === vt.defaultSpeed);
          if (isSpeedDefault || !prev.averageSpeed || currentSpeed <= 0) {
            newState.averageSpeed = selectedVehicleType.defaultSpeed.toString();
          }
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'breaks' && !checked) {
        newState.breakInterval = '2';
        newState.breakDuration = '15';
      }
      
      if (name === 'tollRoads' && !checked) {
        newState.tollRoadsCost = '0';
      }
      
      if (name === 'includeStops' && !checked) {
        newState.stops = [];
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [vehicleTypes, errors, setErrors, setFormData]);

  // Обработчик добавления остановки
  const handleAddStop = (stop) => {
    if (!stop.name || !stop.duration) return;
    
    setFormData(prev => ({
      ...prev,
      stops: [...prev.stops, stop]
    }));
  };

  // Обработчик удаления остановки
  const handleRemoveStop = (index) => {
    setFormData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index)
    }));
  };

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      distance, averageSpeed, breakInterval, breakDuration,
      tollRoads, tollRoadsCost,
      departureTime, departureDate,
      fuelConsumption, fuelPrice,
      numPassengers
    } = formData;

    // Парсинг числовых значений
    const numDistance = parseFloatOrDefault(distance, -1);
    const numAverageSpeed = parseFloatOrDefault(averageSpeed, -1);
    const numBreakInterval = parseFloatOrDefault(breakInterval, -1);
    const numBreakDuration = parseFloatOrDefault(breakDuration, -1);
    const numTollRoadsCost = parseFloatOrDefault(tollRoadsCost, -1);
    const numFuelConsumption = parseFloatOrDefault(fuelConsumption, -1);
    const numFuelPrice = parseFloatOrDefault(fuelPrice, -1);
    const numPassengersCount = parseIntOrDefault(numPassengers, -1);

    // Валидация базовых полей
    if (numDistance <= 0) newErrors.distance = 'Расстояние должно быть положительным.';
    if (!newErrors.distance && numDistance > 10000) newErrors.distance = 'Максимальное расстояние 10000 км.';
    
    if (numAverageSpeed <= 0) newErrors.averageSpeed = 'Скорость должна быть положительной.';
    if (!newErrors.averageSpeed && numAverageSpeed > 200) newErrors.averageSpeed = 'Скорость должна быть не более 200 км/ч.';
    
    // Проверка перерывов
    if (formData.breaks) {
      if (numBreakInterval <= 0) newErrors.breakInterval = 'Интервал должен быть положительным.';
      if (!newErrors.breakInterval && numBreakInterval > 24) newErrors.breakInterval = 'Интервал не может быть больше 24 часов.';
      
      if (numBreakDuration <= 0) newErrors.breakDuration = 'Длительность должна быть положительной.';
      if (!newErrors.breakDuration && numBreakDuration > 180) newErrors.breakDuration = 'Длительность не может быть больше 3 часов (180 минут).';
    }

    // Проверка платных дорог
    if (tollRoads) {
      if (numTollRoadsCost < 0) newErrors.tollRoadsCost = 'Стоимость не может быть отрицательной.';
      if (!newErrors.tollRoadsCost && numTollRoadsCost > 10000) newErrors.tollRoadsCost = 'Стоимость слишком высокая.';
    }

    // Проверка расхода топлива и цены
    if (numFuelConsumption <= 0) newErrors.fuelConsumption = 'Расход должен быть положительным.';
    if (!newErrors.fuelConsumption && numFuelConsumption > 50) newErrors.fuelConsumption = 'Расход не может быть больше 50 л/100км.';
    
    if (numFuelPrice <= 0) newErrors.fuelPrice = 'Цена должна быть положительной.';
    if (!newErrors.fuelPrice && numFuelPrice > 200) newErrors.fuelPrice = 'Цена не может быть больше 200 руб/л.';

    // Проверка количества пассажиров
    if (!isInteger(numPassengers) || numPassengersCount < 1) {
      newErrors.numPassengers = 'Введите целое положительное число.';
    }
    if (!newErrors.numPassengers && numPassengersCount > 50) {
      newErrors.numPassengers = 'Количество пассажиров не может быть больше 50.';
    }

    // Проверка времени и даты отправления
    if (!departureTime) newErrors.departureTime = 'Укажите время отправления.';
    if (!departureDate) newErrors.departureDate = 'Укажите дату отправления.';
    if (departureDate) {
      try {
        const dateObj = new Date(departureDate);
        if (isNaN(dateObj.getTime())) {
          newErrors.departureDate = 'Некорректная дата.';
        }
      } catch (e) {
        newErrors.departureDate = 'Некорректная дата.';
      }
    }

    // Проверка остановок
    if (formData.includeStops && formData.stops.length > 0) {
      formData.stops.forEach((stop, index) => {
        if (!stop.name) {
          newErrors[`stops[${index}].name`] = 'Укажите название остановки.';
        }
        if (!stop.duration || parseFloatOrDefault(stop.duration, -1) <= 0) {
          newErrors[`stops[${index}].duration`] = 'Укажите корректную длительность.';
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета времени в пути
  const calculateRoute = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setRouteDetails(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const numDistance = parseFloatOrDefault(formData.distance);
        const numAverageSpeed = parseFloatOrDefault(formData.averageSpeed);
        const departureDate = new Date(`${formData.departureDate}T${formData.departureTime}`);
        
        // Получаем корректирующие коэффициенты для условий движения
        const roadType = roadTypes.find(r => r.id === formData.roadType) || roadTypes.find(r => r.id === 'mixed');
        const trafficLevel = trafficLevels.find(t => t.id === formData.trafficJams) || trafficLevels.find(t => t.id === 'moderate');
        const weatherCondition = weatherConditions.find(w => w.id === formData.weatherCondition) || weatherConditions.find(w => w.id === 'clear');
        
        // Модифицируем скорость с учетом условий
        const effectiveSpeed = numAverageSpeed 
          * roadType.speedFactor
          / trafficLevel.timeFactor
          / weatherCondition.timeFactor;
        
        // Рассчитываем базовое время в пути (в часах)
        let baseTimeHours = numDistance / effectiveSpeed;
        
        // Добавляем время на перерывы, если они включены
        let breaksTimeHours = 0;
        let numberOfBreaks = 0;
        if (formData.breaks) {
          const breakIntervalHours = parseFloatOrDefault(formData.breakInterval);
          const breakDurationMinutes = parseFloatOrDefault(formData.breakDuration);
          
          numberOfBreaks = Math.floor(baseTimeHours / breakIntervalHours);
          breaksTimeHours = numberOfBreaks * (breakDurationMinutes / 60);
        }
        
        // Добавляем время на остановки
        let stopsTimeHours = 0;
        if (formData.includeStops && formData.stops.length > 0) {
          const totalStopMinutes = formData.stops.reduce((total, stop) => 
            total + parseFloatOrDefault(stop.duration, 0), 0);
          stopsTimeHours = totalStopMinutes / 60;
        }
        
        // Рассчитываем общее время в пути (в часах)
        const totalTimeHours = baseTimeHours + breaksTimeHours + stopsTimeHours;
        
        // Рассчитываем время прибытия
        const arrivalTime = new Date(departureDate);
        arrivalTime.setTime(arrivalTime.getTime() + totalTimeHours * 60 * 60 * 1000);
        
        // Рассчитываем расход топлива и стоимость
        const fuelConsumption = parseFloatOrDefault(formData.fuelConsumption);
        const fuelPrice = parseFloatOrDefault(formData.fuelPrice);
        const fuelNeeded = (numDistance / 100) * fuelConsumption;
        const fuelCost = fuelNeeded * fuelPrice;
        
        // Добавляем стоимость платных дорог если они включены
        let tollCost = 0;
        if (formData.tollRoads) {
          tollCost = parseFloatOrDefault(formData.tollRoadsCost, 0);
        }
        
        // Рассчитываем общую стоимость поездки
        const totalCost = fuelCost + tollCost;
        
        // Готовим детали маршрута
        const routeSegments = [];
        
        // Добавляем первый сегмент (начало)
        routeSegments.push({
          type: 'start',
          location: formData.startPoint || 'Начальная точка',
          time: departureDate,
          distance: 0,
          description: 'Начало поездки'
        });
        
        // Добавляем промежуточные сегменты с перерывами
        let currentTime = new Date(departureDate);
        let distanceCovered = 0;
        
        if (formData.breaks && numberOfBreaks > 0) {
          const breakIntervalHours = parseFloatOrDefault(formData.breakInterval);
          const breakDurationMinutes = parseFloatOrDefault(formData.breakDuration);
          const distancePerInterval = breakIntervalHours * effectiveSpeed;
          
          for (let i = 0; i < numberOfBreaks; i++) {
            // Добавляем время движения
            currentTime = new Date(currentTime.getTime() + breakIntervalHours * 60 * 60 * 1000);
            distanceCovered += distancePerInterval;
            
            // Добавляем сегмент с перерывом
            routeSegments.push({
              type: 'break',
              location: `Перерыв #${i+1}`,
              time: new Date(currentTime),
              distance: distanceCovered,
              duration: breakDurationMinutes,
              description: `Плановый перерыв после ${breakIntervalHours} ч. вождения`
            });
            
            // Добавляем время перерыва
            currentTime = new Date(currentTime.getTime() + breakDurationMinutes * 60 * 1000);
          }
        }
        
        // Добавляем запланированные остановки
        if (formData.includeStops && formData.stops.length > 0) {
          // Распределяем остановки равномерно по маршруту (можно улучшить алгоритм)
          const segmentDistance = numDistance / (formData.stops.length + 1);
          let stopDistanceCovered = distanceCovered;
          
          formData.stops.forEach((stop, index) => {
            // Рассчитываем время в пути до следующей остановки
            const distanceToStop = segmentDistance;
            const timeToStopHours = distanceToStop / effectiveSpeed;
            
            // Добавляем время движения
            currentTime = new Date(currentTime.getTime() + timeToStopHours * 60 * 60 * 1000);
            stopDistanceCovered += distanceToStop;
            
            // Добавляем сегмент с остановкой
            routeSegments.push({
              type: 'stop',
              location: stop.name,
              time: new Date(currentTime),
              distance: stopDistanceCovered,
              duration: parseFloatOrDefault(stop.duration, 0),
              description: `Запланированная остановка: ${stop.name}`
            });
            
            // Добавляем время остановки
            currentTime = new Date(currentTime.getTime() + parseFloatOrDefault(stop.duration, 0) * 60 * 1000);
          });
        }
        
        // Добавляем последний сегмент (прибытие)
        routeSegments.push({
          type: 'finish',
          location: formData.endPoint || 'Конечная точка',
          time: arrivalTime,
          distance: numDistance,
          description: 'Прибытие в пункт назначения'
        });
        
        // Готовим результаты расчетов
        const results = {
          distance: numDistance,
          baseTime: baseTimeHours,
          breaksTime: breaksTimeHours,
          stopsTime: stopsTimeHours,
          totalTime: totalTimeHours,
          effectiveSpeed: effectiveSpeed,
          
          departureTime: departureDate,
          arrivalTime: arrivalTime,
          
          fuelNeeded: fuelNeeded,
          fuelCost: fuelCost,
          tollCost: tollCost,
          totalCost: totalCost,
          
          // Факторы и коэффициенты
          roadFactor: roadType.speedFactor,
          trafficFactor: trafficLevel.timeFactor,
          weatherFactor: weatherCondition.timeFactor,
          
          // Метрики для оценки
          isLongDrive: totalTimeHours > 8,
          isSafeBreaks: formData.breaks && parseFloat(formData.breakInterval) <= 3,
          roadTypeName: roadType.name,
          trafficLevelName: trafficLevel.name,
          weatherConditionName: weatherCondition.name,
          vehicleTypeName: vehicleTypes.find(v => v.id === formData.vehicleType)?.name || 'Автомобиль',
          
          // Информация о пассажирах
          numPassengers: parseInt(formData.numPassengers),
          costPerPerson: totalCost / parseInt(formData.numPassengers)
        };
        
        // Устанавливаем результаты
        setResults(results);
        setRouteDetails(routeSegments);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setRouteDetails(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setRouteDetails, setIsCalculating, roadTypes, trafficLevels, weatherConditions, vehicleTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      startPoint: '',
      endPoint: '',
      distance: '100',
      averageSpeed: '90',
      vehicleType: 'car',
      breaks: true,
      breakInterval: '2',
      breakDuration: '15',
      roadType: 'mixed',
      trafficJams: 'moderate',
      weatherCondition: 'clear',
      departureTime: '08:00',
      departureDate: new Date().toISOString().split('T')[0],
      numPassengers: '1',
      fuelConsumption: '8',
      fuelPrice: '50',
      tollRoads: false,
      tollRoadsCost: '0',
      includeStops: false,
      stops: [],
    });
    setResults(null);
    setRouteDetails(null);
    setErrors({});
    setSaveRouteName('');
    setShowSaveDialog(false);
  };

  // Экспорт маршрута в CSV
  const exportToCSV = () => {
    if (!routeDetails || routeDetails.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки
    const headers = ['Тип', 'Место', 'Время', 'Пройдено км', 'Длительность (мин)', 'Описание'];

    const formatCsvTime = (date) => date.toLocaleTimeString('ru-RU', {hour: '2-digit', minute:'2-digit'});
    const formatCsvDate = (date) => date.toLocaleDateString('ru-RU', {day: '2-digit', month: '2-digit', year: 'numeric'});
    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = routeDetails.map(item => {
      const rowData = [
        item.type === 'start' ? 'Старт' : 
          item.type === 'finish' ? 'Финиш' : 
          item.type === 'break' ? 'Перерыв' : 'Остановка',
        item.location,
        `${formatCsvDate(item.time)} ${formatCsvTime(item.time)}`,
        formatCsvNumber(item.distance),
        item.duration ? formatCsvNumber(item.duration) : '',
        item.description
      ];
      return rowData.join(';');
    });

    // Добавим итоговую информацию в начало файла
    const infoRows = [
      ['Маршрут:', `${formData.startPoint || 'Старт'} - ${formData.endPoint || 'Финиш'}`],
      ['Расстояние:', `${formatCsvNumber(results.distance)} км`],
      ['Общее время в пути:', `${formatCsvNumber(results.totalTime)} ч.`],
      ['Время отправления:', `${formatCsvDate(results.departureTime)} ${formatCsvTime(results.departureTime)}`],
      ['Время прибытия:', `${formatCsvDate(results.arrivalTime)} ${formatCsvTime(results.arrivalTime)}`],
      ['Расход топлива:', `${formatCsvNumber(results.fuelNeeded)} л`],
      ['Общая стоимость:', `${formatCsvNumber(results.totalCost)} руб.`],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'маршрут_поездки.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения маршрута
  const handleSaveRoute = () => {
    if (showSaveDialog) {
      const savedResult = saveRoute(saveRouteName || null);
      if (savedResult) {
        alert(`Маршрут "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveRouteName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateRoute();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Route className="w-6 h-6 text-blue-500" />
          Параметры маршрута
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные параметры маршрута */}
        <RouteParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          roadTypes={roadTypes}
          trafficLevels={trafficLevels}
          weatherConditions={weatherConditions}
        />

        {/* Параметры транспорта */}
        <VehicleParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          vehicleTypes={vehicleTypes}
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Запланированные остановки */}
        <StopsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          handleAddStop={handleAddStop}
          handleRemoveStop={handleRemoveStop}
        />

        {/* Диалог сохранения маршрута */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveRouteName}
                onChange={(e) => setSaveRouteName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveRoute}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Route className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Расчет маршрута...' : 'Рассчитать маршрут'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {routeDetails && routeDetails.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать маршрут (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveRoute}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить маршрут
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;